import { createContext, useCallback, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { en } from "./en.ts";
import { ja } from "./ja.ts";
import type { Locale, Translations } from "./types.ts";

const dictionaries: Record<Locale, Translations> = { en, ja };

interface I18nContextValue {
  locale: Locale;
  t: Translations;
  /** Build a locale-aware path: `/ja/path` for ja, `/path` for en */
  localePath: (path: string) => string;
  /** Strip locale prefix to get the "bare" path */
  stripLocale: (path: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

interface I18nProviderProps {
  locale: Locale;
  children: ReactNode;
}

export function I18nProvider({ locale, children }: I18nProviderProps) {
  document.documentElement.lang = locale;

  const localePath = useCallback(
    (path: string) => {
      return locale === "ja" ? `/ja${path}` : path;
    },
    [locale],
  );

  const stripLocale = useCallback((path: string) => {
    return path.replace(/^\/ja/, "") || "/";
  }, []);

  const value = useMemo(
    () => ({
      locale,
      t: dictionaries[locale],
      localePath,
      stripLocale,
    }),
    [locale, localePath, stripLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
